/**
 * Node Type Utilities
 * Provides helper functions to categorize specific node types into generic categories
 */

// List of node types that are considered connectors
const connectorTypes = [
  'salesforceWriter',
  'salesforceReader',
  'salesforceV2Reader',
  'salesforceBulkWriter',
  'salesforceBulkReader',
  'amazonRDSReader',
  'amazonRDSWriter',
  'amazonRedshiftReader',
  'amazonRedshiftWriter',
  's3Reader',
  's3Writer',
  'amazonSESConnector',
  'awsElasticsearchReader5x',
  'zoomReader',
  'zoomWriter',
  'zoomGraphQLReader',
  'json',
  'ethosReader',
  'ethosWriter',
  'ethosV2AsyncReader',
  'ethosV2Reader',
  'ethosGraphQLReader',
  'httpReader',
  'httpWriter',
  'httpV2Reader',
  'httpXmlConnector',
  'sqlServer',
  'infosilemReader',
  'localFileReader',
  'localFileWriter',
  'localFileImporter',
  'graphqlReader',
  'sftpReader',
  'sftpWriter',
  'lingkWriter',
  'lingkAdapterReader',
  'lingkAdapterWriter',
  'anthologyReader',
  'anthologyWriter',
  'brightSpaceValenceApiReader',
  'brightSpaceValenceApiWriter',
  'jdbcReader',
  'jdbcWriter',
  'blackboardReader',
  'blackboardWriter',
  'canvasReader',
  'canvasWriter',
  'canvasGraphQLReader',
  'colleagueReader',
  'colleagueWriter',
  'microsoftCosmosDbReader',
  'microsoftCosmosDbV2Reader',
  'docuSignReader',
  'edfiReader',
  'edfiWriter',
  'edReadyReader',
  'element451Reader',
  'element451Writer',
  'googleSpreadsheetReader',
  'googleSpreadsheetWriter',
  'googleBigQueryReader',
  'googleBigQueryWriter',
  'leadSquaredReader',
  'leadSquaredWriter',
  'learningStreamReader',
  'learningStreamWriter',
  'moodleReader',
  'moodleWriter',
  'paycomReader',
  'paycomWriter',
  'peoplesoftReader',
  'peoplesoftWriter',
  'logReader',
  'oDataReader',
];

// List of node types that are considered data operations
const dataframeTypes = ['create', 'select', 'insert', 'update'];

// List of node types that are considered commands
const commandTypes = [
  'printCommand',
  'printSchemaCommand',
  'refreshCommand',
  'sleepCommand',
  'stopCommand',
  'stopifzeroCommand',
  'validateCommand',
  'executeCommand',
  'selectCommand',
  'insertCommand',
  'updateCommand',
  'deleteCommand',
  'defaultCommand',
];

// List of node types that are format-related
const formatTypes = [
  'excelReader',
  'excelWriter',
  'jsonArrayWriter',
  'textReader',
  'textWriter',
  'xmlReader',
  'xmlWriter',
  'delimitedReader',
  'delimitedWriter',
];

// List of node types that are task operations
const taskTypes = [
  'sftpOperationdirectoryList',
  'sftpOperationdeleteFile',
  'sftpOperationcopyFile',
  'sftpOperationcopyFileToS3',
  'sftpOperationmoveFile',
  'fileOperationdeleteFileFromS3',
  'fileOperationappend',
  'dataOperationbatch',
  'dataOperationdedup',
];

/**
 * Determine if a node is a connector based on its type
 * @param {String} nodeType - The specific node type
 * @returns {Boolean} True if the node is a connector
 */
export const isConnector = (nodeType) => {
  return connectorTypes.includes(nodeType);
};

/**
 * Determine if a node is a dataframe operation based on its type
 * @param {String} nodeType - The specific node type
 * @returns {Boolean} True if the node is a dataframe operation
 */
export const isDataframeOperation = (nodeType) => {
  return dataframeTypes.includes(nodeType);
};

/**
 * Determine if a node is a command based on its type
 * @param {String} nodeType - The specific node type
 * @returns {Boolean} True if the node is a command
 */
export const isCommand = (nodeType) => {
  return commandTypes.includes(nodeType);
};

/**
 * Determine if a node is a format operation based on its type
 * @param {String} nodeType - The specific node type
 * @returns {Boolean} True if the node is a format operation
 */
export const isFormat = (nodeType) => {
  return formatTypes.includes(nodeType);
};

/**
 * Determine if a node is a task operation based on its type
 * @param {String} nodeType - The specific node type
 * @returns {Boolean} True if the node is a task operation
 */
export const isTask = (nodeType) => {
  return taskTypes.includes(nodeType);
};

/**
 * Get the generic category for a specific node type
 * @param {String} nodeType - The specific node type
 * @returns {String} The generic category (connector, dataframe, command, format, task, or default)
 */
export const getNodeCategory = (nodeType) => {
  if (isConnector(nodeType)) return 'connector';
  if (isDataframeOperation(nodeType)) return 'dataframe';
  if (isCommand(nodeType)) return 'command';
  if (isFormat(nodeType)) return 'format';
  if (isTask(nodeType)) return 'task';
  if (nodeType === 'actionFrame') return 'actionFrame';
  return 'default';
};

/**
 * Check if the node is of a generic category
 * @param {Object} node - The node to check
 * @param {String} category - The generic category to check against
 * @returns {Boolean} True if the node belongs to the category
 */
export const isNodeOfCategory = (node, category) => {
  if (!node || !node.type) return false;

  // If the node's type directly matches the category
  if (node.type === category) return true;

  // Otherwise check if the node's specific type falls into this category
  return getNodeCategory(node.type) === category;
};
